import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ThemeService } from 'weavix-shared/services/themeService';
import { FilterResultType, TableFilterCategory, TableFilterResult } from 'weavix-shared/models/table.model';
import { AutoUnsubscribe } from 'weavix-shared/utils/utils';
import { TableService } from '../table.service';
import { DateRange } from 'weavix-shared/models/dvr.model';
import { CommonModule } from '@angular/common';
import { FilterHeaderComponent } from 'components/filter-header/filter-header.component';
import { IconComponent } from 'components/icon/icon.component';
import { DateRangePickerComponent } from 'components/date-range-picker/date-range-picker.component';
import { TableFilterRowComponent } from '../table-filter-row/table-filter-row.component';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { sortAlphabetical } from '@weavix/utils/src/sort';

export interface TableFilterSearchConfig {
    enabled: boolean;
    category: TableFilterCategory;
    placeholderKey?: string;
}

@AutoUnsubscribe()
@Component({
    selector: 'app-table-filter',
    templateUrl: './table-filter.component.html',
    styleUrls: ['./table-filter.component.scss', '../table-filter-row/table-filter-row.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        DateRangePickerComponent,
        FilterHeaderComponent,
        IconComponent,
        TableFilterRowComponent,
    ],
})

export class TableFilterComponent implements OnChanges, OnInit {
    @Input() headerTitle: string;
    @Input() skipSortFilters: boolean = false;
    @Input() filterResults: { [key: string]: TableFilterResult } = {};
    @Input() multiCategorySelect = false;
    @Input() hasDateRangeFilter = false;
    @Input() activeDateRange: DateRange;

    selectedDateRange: DateRange;
    dateRangeFilter: TableFilterResult = {
        name: 'date',
        key: 'date',
        category: TableFilterCategory.Date,
        type: FilterResultType.Date,
        selected: true,
    };

    loading: boolean = false;
    lightTheme: boolean;

    get titleKey(): string { return this.headerTitle ?? 'generics.filters'; }
    get filtersCount(): number { return Object.keys(this.filterResults ?? []).length ? this.tableService.filtersCount : 0; }
    get filterResultsToDisplay() {
        let filters = Object.values(this.filterResults);
        if (!this.skipSortFilters) filters = filters.sort((a, b) => a.name < b.name ? -1 : 1);
        // filtered options with no children should always be at the bottom of the list
        return filters.filter(f => f.children).concat(filters.filter(f => !f.children));
    }

    constructor(
        public tableService: TableService,
    ) {}

    getChildren(res: TableFilterResult) {
        let children = Object.values(res.children ?? {});

        if (res.skipSortChildren) return children;
        const firstItem = children.find(child => child.key === 'first-item') || null;
        const lastItem = children.find(child => child.key === 'last-item') || null;

        children = children.filter(child => child.key !== 'first-item' && child.key !== 'last-item');

        children = sortAlphabetical(children, 'name');

        if (firstItem) children.unshift(firstItem);
        if (lastItem) children.push(lastItem);

        return children;
    }

    ngOnInit() {
        this.loading = true;
        this.lightTheme = ThemeService.getLightTheme();
        this.loading = false;
    }

    ngOnChanges(changes: SimpleChanges) {
        this.loading = true;
        if (changes.filterResults) {
            this.checkForEmptyFilters();
        }
        if (changes.activeDateRange) {
            this.selectedDateRange = this.activeDateRange;
        }
        this.loading = false;
    }

    handleDateRangeFilterClick(): void {
        if (!this.dateRangeFilter) return;
        if (this.dateRangeFilter.disabled && this.dateRangeFilter.disabled()) return;
        else if (this.dateRangeFilter?.customClickAction) this.dateRangeFilter.customClickAction();
        else {
            this.dateRangeFilter.selected = !this.dateRangeFilter.selected;
            this.dateRangeFilter.setSelected?.(this.dateRangeFilter.selected, true, true);
        }
    }

    handleDateRangeFilterChange(dateRange: DateRange): void {
        this.tableService.selectedDateFilters[TableFilterCategory.Date] = dateRange;
        this.tableService.filterUpdate$.next({ resetFilters: false });
    }

    handleClearFilters() {
        this.selectedDateRange = this.activeDateRange;
        if (this.selectedDateRange) {
            this.dateRangeFilter.selected = false;
            delete this.tableService.selectedDateFilters[TableFilterCategory.Date];
            this.tableService.filterUpdate$.next({ resetFilters: false });
        }
        this.deselectCategory({ children: this.filterResults } as any);
    }

    handleRowClick(parent: TableFilterResult, cat: TableFilterResult, click: boolean, depth: number): void {
        if (!cat) return;

        if (!this.multiCategorySelect) this.deselectCategory(parent, cat);
        if (depth > 0 && !parent.multiselect) this.deselectCategory(parent, cat);
        cat.selected = click ? true : !cat.selected;
        cat.setSelected?.(cat.selected, true, click);
    }

    deselectCategory(cat: TableFilterResult, ignore?): void {
        if (cat.children) {
            Object.values(cat.children).forEach(subCat => {
                if (subCat === ignore) return;
                this.deselectCategory(subCat);
                subCat.selected = false;
                subCat.setSelected?.(false);
                subCat.hidden = false;
            });
        }
    }

    checkForEmptyFilters() {
        let allEmpty: boolean = true;
        for (const val of Object.values(this.filterResults)) {
            if (Object.values(val.children).length) {
                allEmpty = false;
                break;
            }
        }

        if (allEmpty) return;
    }

    hideNestedParent(res: TableFilterResult, parent: TableFilterResult): boolean {
        // nested filters will always have children. Show if there are no children
        if (!res.children) return false;
        // not nested filters don't have a parent.key. These should always show.
        if (!parent.key) return false;
        // if the nested parent filter is the only one under the super parent, hide it and just show the filters
        return Object.keys(parent.children).length === 1;
    }

    isCategorySelected(res: TableFilterResult): boolean {
        return Object.values(res.children ?? {}).some(child => child.selected || this.isCategorySelected(child));
    }
}
